<script lang="ts">
  import CoordinatorWorkspace from './CoordinatorWorkspace.svelte';
  import QueryStatus from '$lib/components/ui/QueryStatus.svelte';
  import { relativeTime, formatIST } from '$lib/utils/dates';
  import { storedColor } from '$lib/utils/colors';
  import { toast } from '$lib/state/toast.svelte';
  import { onDestroy } from 'svelte';
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { Download, ArrowLeft } from 'lucide-svelte';
  import { leadsApi } from '$lib/api/leads';
  import { exportLeadsCSV } from '$lib/api/admin';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import Pagination from '$lib/components/ui/Pagination.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import LeadDetail from './LeadDetail.svelte';
  import type { Lead, LeadStatus } from '$lib/types';

  const { onNavigate }: { onNavigate: (tab: string) => void } = $props();

  const queryClient = useQueryClient();
  let showNew = $state(false);
  let intakeError = $state('');
  let intakeGeneration = $state(0);
  let active = true;
  onDestroy(() => { active = false; });
  let intake = $state({ vertical_id: undefined as number | undefined, name: '', email: '', phone: '', message: '' });
  type IntakeSubmission = { generation: number; originId: number | null; body: typeof intake };
  function matchesIntake(submission: IntakeSubmission) {
    return active && showNew && intakeGeneration === submission.generation && route.current.tab === 'leads' && selectedLeadId === submission.originId;
  }
  function closeIntake() { intakeGeneration++; showNew = false; }
  function openIntake() { intakeGeneration++; intake = { vertical_id: verticalId ?? session.me?.verticals[0]?.id, name: '', email: '', phone: '', message: '' }; intakeError = ''; showNew = true; }
  $effect(() => { if (session.consumeSignal('leads')?.mode === 'create') openIntake(); });
  const intakeMutation = createMutation(() => ({
    mutationFn: (submission: IntakeSubmission) => leadsApi.create(submission.body),
    onSuccess: (lead, submission) => {
      queryClient.setQueryData(['lead-detail-fallback', lead.id], lead);
      onLeadChanged();
      if (!matchesIntake(submission)) return;
      closeIntake(); status = 'new'; openLead(lead);
    },
    onError: (err: unknown, submission) => { if (matchesIntake(submission)) intakeError = getErrorMessage(err); }
  }));

  let page = $state(1);
  let status = $state<LeadStatus | '' | 'guides'>('new');
  let category = $state('');
  let spamOnly = $state(false);
  // Selected id lives in the URL hash (#/leads/<id>) so Back/Forward and
  // refresh — including the mobile stacked-column "back" case — all work.
  const selectedLeadId = $derived(route.current.tab === 'leads' ? (route.current.id ?? null) : null);

  const verticalId = $derived(
    session.selectedVertical === 'all' ? undefined : session.selectedVertical
  );

  const tabDefs: Array<{ value: LeadStatus | '' | 'guides'; label: string }> = [
    { value: 'new', label: 'New' },
    { value: 'guides', label: 'Guide downloads' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'converted', label: 'Converted' },
    { value: 'discarded', label: 'Discarded' },
    { value: '', label: 'All' }
  ];

  // Cheap per-tab totals: `count` reflects the filtered total server-side
  // regardless of per_page, so per_page:1 is enough to read it.
  const tabCounts = tabDefs.map((t) => ({
    value: t.value,
    label: t.label,
    query: createQuery(() => ({
      queryKey: ['leads-count', { status: t.value, category, spamOnly, verticalId }],
      queryFn: () =>
        leadsApi.list({
          page: 1,
          per_page: 1,
          status: t.value === 'guides' ? undefined : t.value || undefined,
          purpose: t.value === 'guides' ? 'guide_download' : undefined,
          category: category || undefined,
          spam: spamOnly ? 'true' : undefined,
          vertical_id: verticalId
        })
    }))
  }));

  const listQuery = createQuery(() => ({
    queryKey: ['leads', { page, status, category, spamOnly, verticalId }],
    queryFn: () =>
      leadsApi.list({
        page,
        per_page: 25,
        status: status === 'guides' ? undefined : status || undefined,
        purpose: status === 'guides' ? 'guide_download' : undefined,
        category: category || undefined,
        spam: spamOnly ? 'true' : undefined,
        vertical_id: verticalId
      })
  }));

  $effect(() => {
    status;
    category;
    spamOnly;
    verticalId;
    page = 1;
  });

  const cachedLead = $derived(
    listQuery.data?.results.find((l) => l.id === selectedLeadId) ?? null
  );

  // ponytail: direct-link/refresh into #/leads/<id> when that lead isn't on
  // the currently loaded page/filter — fetch it standalone instead of
  // forcing the list's filters to match. Bounces back to the list on
  // 404/other error rather than crashing on a stale id.
  const leadFallbackQuery = createQuery(() => ({
    queryKey: ['lead-detail-fallback', selectedLeadId],
    queryFn: () => leadsApi.get(selectedLeadId as number),
    enabled: selectedLeadId != null && cachedLead == null
  }));

  const selectedLead = $derived(cachedLead ?? (leadFallbackQuery.data?.id === selectedLeadId ? leadFallbackQuery.data : null));

  $effect(() => {
    if (selectedLeadId != null && cachedLead == null && leadFallbackQuery.isError) {
      route.replace({ tab: 'leads' });
    }
  });

  function openLead(row: Lead) {
    route.push({ tab: 'leads', id: row.id });
  }
  function closeLead() {
    route.push({ tab: 'leads' });
  }
  function onLeadChanged() {
    queryClient.invalidateQueries({ queryKey: ['leads'] });
    queryClient.invalidateQueries({ queryKey: ['leads-count'] });
  }
  function onLeadUpdated(updated: Lead) {
    queryClient.setQueryData(['lead-detail-fallback', updated.id], updated);
    queryClient.setQueriesData({ queryKey: ['leads'] }, (old: typeof listQuery.data) =>
      old ? { ...old, results: old.results.map((l) => (l.id === updated.id ? updated : l)) } : old
    );
  }

  async function doExport() {
    try {
      await exportLeadsCSV({
        status: status === 'guides' ? undefined : status || undefined,
        purpose: status === 'guides' ? 'guide_download' : undefined,
        category: category || undefined,
        spam: spamOnly ? 'true' : undefined,
        vertical_id: verticalId
      });
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  function timeAgo(iso: string): string {
    return relativeTime(iso);
  }

  // ponytail: category colors have no backend field of their own — cycle a
  // small deterministic palette by hashing the category string.
  const CAT_PALETTE = [
    { bg: '#E3F0F1', fg: '#226A73' },
    { bg: '#ECF8CC', fg: '#5c7a00' },
    { bg: '#FBF0DC', fg: '#8a5c00' },
    { bg: '#E4EEEF', fg: '#5F9CA3' }
  ];
  function categoryStyle(cat: string) {
    if (!cat) return { bg: '#F1F0EF', fg: '#666666' };
    const fg = storedColor(session.me?.verticals.find(v => v.id === verticalId)?.settings?.category_colors?.[cat]);
    return { bg: `${fg}1F`, fg };
  }
  const knownCategories = $derived([...new Set([
    category,
    ...(listQuery.data?.results ?? []).map(lead => lead.category),
    ...(session.me?.verticals ?? []).filter(v => verticalId === undefined || v.id === verticalId).flatMap(v => Object.keys(v.settings?.category_colors ?? {}))
  ].filter(Boolean))].sort());
</script>

<CoordinatorWorkspace />
<div class="flex h-full min-h-0">
  <!-- list column: full width on mobile until a lead is selected, 400px on desktop -->
  <div
    class="{selectedLeadId ? 'hidden md:flex' : 'flex'} w-full shrink-0 flex-col border-toyama-cloudy-gray bg-white md:w-[400px] md:border-r"
  >
    <div class="border-b border-toyama-cloudy-gray px-3 pt-3.5 md:px-4">
      <button type="button" class="btn-primary mb-3" onclick={openIntake}>New lead</button>
      <div class="flex flex-wrap gap-x-1">
        {#each tabCounts as t (t.value)}
          <button
            type="button"
            class="lead-tab shrink-0 {status === t.value ? 'active' : ''}"
            onclick={() => (status = t.value)}
          >
            {t.label}
            <span
              class="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 font-heading text-[10.5px] font-bold {status ===
              t.value
                ? 'bg-navy text-white'
                : 'bg-[#EFEFEF] text-dark-gray'}"
            >
              {t.query.data?.count ?? 0}
            </span>
          </button>
        {/each}
      </div>
      <div class="flex items-center gap-2 py-2.5">
        <select class="input flex-1 !py-1.5 text-[12.5px]" aria-label="Filter by category" bind:value={category}><option value="">All categories</option>{#each knownCategories as value}<option value={value}>{value}</option>{/each}</select>
        <button
          type="button"
          class="tym-chip !min-h-0 !px-2.5 !py-1.5 {spamOnly ? '!bg-[var(--tym-danger)] !border-[var(--tym-danger)] !text-white' : ''}"
          onclick={() => (spamOnly = !spamOnly)}
        >
          Spam
        </button>
        {#if session.isAdmin}
          <button type="button" class="btn-secondary !h-10 !w-10 !p-0" onclick={doExport} aria-label="Export CSV" title="Export CSV">
            <Download class="h-4 w-4" />
          </button>
        {/if}
      </div>
    </div>

    <div class="flex-1 overflow-y-auto">
      {#if listQuery.isLoading}
        <QueryStatus loading label="leads" />
      {:else if listQuery.isError}
        <QueryStatus error={listQuery.error} label="leads" onRetry={() => listQuery.refetch()} />
      {:else if listQuery.data}
        {#each listQuery.data.results as row (row.id)}
          {@const cs = categoryStyle(row.category)}
          <button type="button" class="lead-row {selectedLeadId === row.id ? 'selected' : ''}" onclick={() => openLead(row)}>
            <div class="flex items-center gap-[9px]">
              {#if row.status === 'new'}
                <span class="h-2 w-2 shrink-0 rounded-full bg-toyama-lime"></span>
              {/if}
              <span class="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-navy">
                {row.name || row.email || '(no name)'}
              </span>
              <time class="shrink-0 text-[11px] text-dark-gray" datetime={row.created_at} title={formatIST(row.created_at)}>{timeAgo(row.created_at)}</time>
            </div>
            {#if row.message}
              <div class="mt-1 line-clamp-1 text-[12px] leading-[1.4] text-[#666]">{row.message}</div>
            {/if}
            <div class="mt-[7px] flex flex-wrap items-center gap-1.5">
              {#if row.purpose === 'guide_download'}
                <span class="tym-badge" style="background:#E3F0F1;color:#226A73">Guide · {row.guide_audience === 'homeowner' ? 'Homeowner' : 'Architect / designer'}</span>
                <span class="text-[11px] text-dark-gray">{row.contact_requested ? 'Email help requested' : 'Download only · no contact'}</span>
              {/if}
              {#if row.category}
                <span class="tym-badge" style="background:{cs.bg}; color:{cs.fg}">{row.category}</span>
              {/if}
              {#if row.phone}<span class="text-[11.5px] font-semibold text-navy">{row.phone}</span>{/if}
              {#if row.page}<span class="hidden text-[11px] text-dark-gray sm:inline">{row.page}</span>{/if}
              {#if row.spam}<span class="tym-badge" style="background:#fbece9; color:var(--tym-danger)">spam</span>{/if}
            </div>
          </button>
        {:else}
          <p class="tym-empty-state">No leads in this view.</p>
        {/each}
      {/if}
    </div>

    {#if listQuery.data}
      <div class="border-t border-toyama-cloudy-gray px-3">
        <Pagination
          page={listQuery.data.page}
          perPage={listQuery.data.per_page}
          count={listQuery.data.count}
          onChange={(p) => (page = p)}
        />
      </div>
    {/if}
  </div>

  <!-- detail column -->
  <div class="{selectedLeadId ? 'flex' : 'hidden md:flex'} min-w-0 flex-1 flex-col overflow-y-auto">
    {#if selectedLead}
      <button
        type="button"
        class="flex min-h-[44px] items-center gap-1.5 px-5 pt-4 text-[12.5px] font-semibold text-toyama-teal md:hidden"
        onclick={closeLead}
      >
        <ArrowLeft class="h-4 w-4" /> Back to leads
      </button>
      {#key selectedLead.id}
        <LeadDetail lead={selectedLead} {onNavigate} onChanged={onLeadChanged} onUpdated={onLeadUpdated} />
      {/key}
    {:else}
      <div class="flex flex-1 items-center justify-center text-sm text-dark-gray">No lead selected in this view.</div>
    {/if}
  </div>
</div>

{#if showNew}
  <Modal title="New lead" onClose={closeIntake}>
    <form class="space-y-4" oninput={() => { intakeGeneration++; intakeError = ''; }} onsubmit={(e) => { e.preventDefault(); intakeError = ''; intakeMutation.mutate({ generation: intakeGeneration, originId: selectedLeadId, body: { ...intake } }); }}>
      <p class="text-sm text-dark-gray">Record an inbound phone or email enquiry. Save it in the inbox first; no notification is sent.</p>
      <label class="label" for="intake-team">Team</label>
      <select id="intake-team" class="input" required bind:value={intake.vertical_id}>{#each session.me?.verticals ?? [] as v (v.id)}<option value={v.id}>{v.name}</option>{/each}</select>
      <label class="label" for="intake-name">Customer name</label><input id="intake-name" class="input" required maxlength="200" bind:value={intake.name} />
      <label class="label" for="intake-email">Email (email or phone required)</label><input id="intake-email" class="input" type="email" maxlength="254" bind:value={intake.email} />
      <label class="label" for="intake-phone">Phone</label><input id="intake-phone" class="input" type="tel" maxlength="50" bind:value={intake.phone} />
      <label class="label" for="intake-message">Enquiry</label><textarea id="intake-message" class="input" required maxlength="20000" rows="5" bind:value={intake.message}></textarea>
      {#if intakeError}<p class="text-sm text-red-600">{intakeError}</p>{/if}
      <button class="btn-primary" type="submit" disabled={intakeMutation.isPending}>{intakeMutation.isPending ? 'Saving…' : 'Save lead'}</button>
    </form>
  </Modal>
{/if}
